import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState, type MouseEventHandler } from 'react';
import { TransformComponent, TransformWrapper, type ReactZoomPanPinchContentRef } from 'react-zoom-pan-pinch';

import { resolveTooltipAt } from '../../model/screencast-tooltips/screencastTooltipResolver';
import type { ResolvedTooltip, ScreencastTooltips } from '../../model/screencast-tooltips/types';

import { HighlightOverlay } from '../screencast-tooltip/HighlightOverlay';
import { ScreencastTooltip } from '../screencast-tooltip/ScreencastTooltip';

const scaleUp = true;
const zoomFactor = 8;

type TransformState = {
  scale: number;
  positionX: number;
  positionY: number;
};

type TooltipScreenPosition = {
  x: number;
  y: number;
};

type BroadcastImageProps = {
  src: string;
  alt: string;
  isTooltipVisible: boolean;
  screencastTooltips?: ScreencastTooltips | null;
};

export const BroadcastImage = observer(
  // eslint-disable-next-line mobx/no-anonymous-observer
  forwardRef(function BroadcastImage({ src, alt, screencastTooltips, isTooltipVisible }: BroadcastImageProps, ref) {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const reactZoomPanPinchContent = useRef<ReactZoomPanPinchContentRef | null>(null);

    const [containerWidth, setContainerWidth] = useState<number>(0);
    const [containerHeight, setContainerHeight] = useState<number>(0);

    const [imageNaturalWidth, setImageNaturalWidth] = useState<number>(0);
    const [imageNaturalHeight, setImageNaturalHeight] = useState<number>(0);
    const [transformState, setTransformState] = useState<TransformState>({
      scale: 1,
      positionX: 0,
      positionY: 0,
    });
    const [resolvedTooltip, setResolvedTooltip] = useState<ResolvedTooltip | null>(null);
    const [tooltipScreenPosition, setTooltipScreenPosition] = useState<TooltipScreenPosition | null>(null);

    const imageScale = useMemo(() => {
      if (containerWidth === 0 || containerHeight === 0 || imageNaturalWidth === 0 || imageNaturalHeight === 0)
        return 0;
      const scale = Math.min(containerWidth / imageNaturalWidth, containerHeight / imageNaturalHeight);
      return scaleUp ? scale : Math.max(scale, 1);
    }, [containerWidth, containerHeight, imageNaturalWidth, imageNaturalHeight]);

    useEffect(() => {
      if (container) {
        const { width, height } = container.getBoundingClientRect();
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setContainerWidth(width);
        setContainerHeight(height);

        const resizeObserver = new ResizeObserver((entries) => {
          window.requestAnimationFrame((): void | undefined => {
            if (!Array.isArray(entries) || !entries.length) {
              return;
            }

            const { height, width } = container.getBoundingClientRect();
            setContainerWidth(width);
            setContainerHeight(height);
          });
        });

        resizeObserver.observe(container);

        return () => {
          resizeObserver.disconnect();
        };
      }
    }, [ref, container]);

    const handleImageOnLoad = (image: HTMLImageElement) => {
      setImageNaturalWidth(image.naturalWidth);
      setImageNaturalHeight(image.naturalHeight);
    };

    useEffect(() => {
      const image = new Image();
      image.onload = () => handleImageOnLoad(image);
      image.src = src;
    }, [src]);

    useImperativeHandle(ref, () => {
      return {
        zoomIn: reactZoomPanPinchContent.current?.zoomIn,
        zoomOut: reactZoomPanPinchContent.current?.zoomOut,
        setTransform: reactZoomPanPinchContent.current?.setTransform,
        resetTransform: reactZoomPanPinchContent.current?.resetTransform,
        centerView: reactZoomPanPinchContent.current?.centerView,
        zoomToElement: reactZoomPanPinchContent.current?.zoomToElement,
      };
    });

    const handleMouseLeave = () => {
      setResolvedTooltip(null);
      setTooltipScreenPosition(null);
    };

    const handleMouseMove: MouseEventHandler<HTMLDivElement> = (event) => {
      if (!isTooltipVisible || !screencastTooltips || transformState.scale <= 0) {
        setResolvedTooltip(null);
        setTooltipScreenPosition(null);
        return;
      }

      const wrapperRect = event.currentTarget.getBoundingClientRect();
      const wrapperX = event.clientX - wrapperRect.left;
      const wrapperY = event.clientY - wrapperRect.top;

      const x = (wrapperX - transformState.positionX) / transformState.scale;
      const y = (wrapperY - transformState.positionY) / transformState.scale;

      if (x < 0 || y < 0 || x > imageNaturalWidth || y > imageNaturalHeight) {
        setResolvedTooltip(null);
        setTooltipScreenPosition(null);
        return;
      }

      const nextResolvedTooltip = resolveTooltipAt(screencastTooltips, x, y);
      if (!nextResolvedTooltip) {
        setResolvedTooltip(null);
        setTooltipScreenPosition(null);
        return;
      }

      setResolvedTooltip(nextResolvedTooltip);
      setTooltipScreenPosition({
        x: event.clientX,
        y: event.clientY,
      });
    };

    const visibleResolvedTooltip = isTooltipVisible ? resolvedTooltip : null;
    const visibleTooltipScreenPosition = isTooltipVisible ? tooltipScreenPosition : null;

    return (
      <div style={{ height: '100%', position: 'relative' }} ref={(el: HTMLDivElement | null) => setContainer(el)}>
        {imageScale > 0 && (
          <TransformWrapper
            key={`${containerWidth}x${containerHeight}`}
            initialScale={imageScale}
            minScale={imageScale}
            maxScale={imageScale * zoomFactor}
            centerOnInit
            ref={reactZoomPanPinchContent}
            onInit={(instance) => {
              setTransformState(instance.state);
            }}
            onTransformed={(_, state) => {
              setTransformState(state);
            }}
          >
            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
              }}
              wrapperProps={{
                onMouseMove: handleMouseMove,
                onMouseLeave: handleMouseLeave,
              }}
            >
              <div style={{ position: 'relative', lineHeight: 0 }}>
                <img alt={alt} src={src} style={{ display: 'block' }} />
                {visibleResolvedTooltip && (
                  <HighlightOverlay
                    imageNaturalWidth={imageNaturalWidth}
                    imageNaturalHeight={imageNaturalHeight}
                    areas={visibleResolvedTooltip.highlightAreas}
                  />
                )}
              </div>
            </TransformComponent>
          </TransformWrapper>
        )}
        <ScreencastTooltip tooltip={visibleResolvedTooltip} cursorPosition={visibleTooltipScreenPosition} />
      </div>
    );
  }),
);
